import { meiliHost, meiliApiKey, index as productIndexName } from "./algoliaSettings";

// Namespaced per catalog (curation-rules__<productIndex>) to match
// brekz-search-manager's curationIndexFor() — the admin app writes rules there, not to a
// bare "curation-rules" index, so this must derive from the same per-shop product index
// rather than a fixed name that can drift out of sync with it.
function curationIndexUid() {
    const productIndex = productIndexName();
    return productIndex ? `curation-rules__${productIndex}` : null;
}

function escapeFilterValue(v) {
    return String(v).replace(/"/g, '\\"');
}

function isWithinWindow(doc, now) {
    if (doc.endAtTs != null && doc.endAtTs < now) return false;
    if (doc.startAtTs != null && doc.startAtTs > now) return false;
    return true;
}

// Reshapes our curation-rules banner field into the shape AlgoliaSearchResults.vue's
// template already expects (Algolia's old Query Rule userData shape) — kept here rather
// than changing the production template, since the schema is shared with the admin tool
// and brekz-website.
function reshapeBanner(doc) {
    if (!doc || !doc.banner) return null;
    return {
        link: { url: doc.banner.linkUrl },
        image: { urls: [{ url: doc.banner.imageUrl }], alt: doc.banner.title },
    };
}

// Looks up global/query/category-scoped curation rules for the current search
// context in one multi-search call, independent of the instant-meilisearch searchClient
// driving the actual products query — this only ever targets curationIndexUid().
export async function fetchCurationContext({ trigger, categoryId }) {
    const host = meiliHost();
    const key = meiliApiKey();
    const indexUid = curationIndexUid();
    if (!host || !key || !indexUid) {
        return { banner: null, hiddenIds: [], exclusiveIds: null };
    }

    const now = Date.now();
    const windowFilter = `(startAtTs IS NULL OR startAtTs <= ${now}) AND (endAtTs IS NULL OR endAtTs >= ${now})`;

    const queries = [
        {
            indexUid,
            q: "",
            filter: `scope = global AND active = true AND ${windowFilter}`,
            sort: ["priority:asc"],
            limit: 10,
        },
    ];
    if (trigger) {
        queries.push({
            indexUid,
            q: "",
            filter: `scope = query AND active = true AND trigger = "${escapeFilterValue(trigger)}" AND ${windowFilter}`,
            sort: ["priority:asc"],
            limit: 10,
        });
    }
    if (categoryId) {
        queries.push({
            indexUid,
            q: "",
            filter: `scope = category AND active = true AND trigger = "${escapeFilterValue(String(categoryId))}" AND ${windowFilter}`,
            sort: ["priority:asc"],
            limit: 10,
        });
    }

    let curationDocs = [];
    try {
        const res = await fetch(`${host}/multi-search`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${key}` },
            body: JSON.stringify({ queries }),
        });
        const data = await res.json();
        if (res.ok) {
            curationDocs = (data.results || []).flatMap((r) => r.hits || []).filter((d) => isWithinWindow(d, now));
        }
    } catch {
        // curation lookup is a non-critical enhancement — fail quietly, products search is unaffected
    }

    const exclusiveDoc = curationDocs
        .filter((d) => d.exclusive && d.pinnedProductIds && d.pinnedProductIds.length)
        .sort((a, b) => (a.priority ?? 999) - (b.priority ?? 999))[0] || null;

    const hiddenIds = exclusiveDoc
        ? []
        : curationDocs.flatMap((d) => d.hiddenProductIds || []);

    const banners = curationDocs.filter((d) => d.banner).sort((a, b) => (a.priority ?? 999) - (b.priority ?? 999));

    return {
        banner: reshapeBanner(banners[0]),
        hiddenIds,
        exclusiveIds: exclusiveDoc ? exclusiveDoc.pinnedProductIds.map((p) => p.id) : null,
    };
}

// Composes the base category/query filter with hidden/exclusive constraints from a
// fetched curation context, per the storefront design (folded into the same filter
// string bound to <ais-configure>, not a post-hoc filter of already-fetched hits, so
// <ais-stats>/<ais-pagination> stay consistent with what's actually rendered).
export function composeSearchFilters(baseFilter, context) {
    if (context.exclusiveIds && context.exclusiveIds.length) {
        const idsCsv = context.exclusiveIds.map((id) => `"${id}"`).join(",");
        return `id IN [${idsCsv}]`;
    }
    if (context.hiddenIds && context.hiddenIds.length) {
        const idsCsv = context.hiddenIds.map((id) => `"${id}"`).join(",");
        const hideClause = `id NOT IN [${idsCsv}]`;
        return baseFilter ? `(${baseFilter}) AND ${hideClause}` : hideClause;
    }
    return baseFilter;
}
